import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ApiService from "../../services/ApiService";
import { Button, Col, Row } from "react-bootstrap";
import { ChevronLeft, ChevronRight } from "lucide-react";
import NoDataAnimation from "../../assets/animations/No Result Green theme.json"
import Lottie from "lottie-react";
import Paginator from "../../components/Paginator";
import { useTranslation } from "react-i18next";
import Image from "../../components/Image";

const HistoryCard = ({ item }) => {
  const statusStyles = {
    paid: { bg: "#D3FDEA", color: "#5F8D73" },
    pending: { bg: "#FEE8D3", color: "#D07B1F" },
    failed: { bg: "#FDE3E3", color: "#D15F5F" },
  };
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const { color, bg } =
    statusStyles[item?.project_id?.pay_status?.toLowerCase()] ||
    statusStyles["pending"];

  function formatSingleDate(isoDate) {
    if (!isoDate) return "N/A";
    const date = new Date(isoDate);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    return `${day} ${month}`;
  }

  return (
    <div
      className="inter-font d-flex align-items-center border-bottom py-3 px-4"
      style={{
        minWidth: "850px",
        gap: "40px",
        justifyContent: "space-between",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      {/* Company Logo + Info */}
      <div 
        className="d-flex align-items-center" 
        style={{ 
          minWidth: "250px",
          flexDirection: isRTL ? "row-reverse" : "row",
          gap: "15px"
        }}
      >
        <Image
          src={item?.project_id?.company_id?.logo}
          className="rounded-circle"
          style={{ 
            width: "54px", 
            height: "54px", 
            objectFit: "cover"
          }}
          alt="Profile"
        />
        <div style={{ 
          textAlign: isRTL ? "right" : "left"
        }}>
          <h5 className="mb-1" style={{ fontSize: "16px", fontWeight: "600" }}>
            {item?.project_id?.title || "N/A"}
          </h5>
          <p className="mb-0" style={{ fontSize: "15px", color: "#777474" }}>
            {item?.project_id?.company_id?.company_name || "N/A"}
          </p>
        </div>
      </div>

      {/* Project Dates */}
      <div style={{ 
        minWidth: "150px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t('c_d_table_c4')} {/* Project Dates */}
        </h6>
        <p style={{ 
          fontSize: "14px", 
          color: "#777474",
          direction: "ltr", // Force LTR for dates
          textAlign: isRTL ? "right" : "left"
        }}>
          {formatSingleDate(item.start_date)} - {formatSingleDate(item.end_date)}
        </p>
      </div>

      {/* Earnings - FIXED */}
      <div style={{ 
        minWidth: "120px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t('t_d_text85')} {/* Amount */}
        </h6>
        <p style={{ 
          fontSize: "14px", 
          color: "#777474",
          direction: "ltr", // Force LTR direction
          unicodeBidi: "plaintext" // Prevent bidirectional text reordering
        }}>
          {item.total_amount} AED
        </p>
      </div>

      {/* Payment Status */}
      <div style={{ 
        minWidth: "120px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t('c_d_table_c8')} {/* Payment */}
        </h6>
        <button
          className="btn border-0"
          style={{
            color,
            backgroundColor: bg,
            width: "85px",
            height: "27px",
            fontSize: "12px",
            fontWeight: 500,
            borderRadius: "6px",
            direction: "ltr" // Force LTR for status text if needed
          }}
        >
          {item?.project_id?.pay_status || "N/A"}
        </button>
      </div>
    </div>
  );
};



const History = () => {
  const [currentPage, setCurrentPage] = useState(1);



  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    current_page: 1,
  });
  const [historyData, setHistoryData] = useState([]);

  const fetchHistoryData = async (page = 1) => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "GET",
        url: `getHistory?page=${page}`,
      });

      const data = res.data;
      if (data.status) {
        console.log("data -", data.data.projects);
        setHistoryData(data.data.projects);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      console.error("Fetch Job Error:", err);
      toast.error("Failed to fetch jobs.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (
      newPage >= 1 &&
      newPage <= pagination.total_pages &&
      newPage !== pagination.current_page
    ) {
      fetchHistoryData(newPage);
    }
  };

  const { current_page, total_pages } = pagination;

  // Generate page numbers (for large datasets show 1,2,3,...,last)
  const getPages = () => {
    const pages = [];

    if (total_pages <= 5) {
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (current_page > 3) {
        pages.push("...");
      }
      for (
        let i = Math.max(2, current_page - 1);
        i <= Math.min(total_pages - 1, current_page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (current_page < total_pages - 2) {
        pages.push("...");
      }
      pages.push(total_pages);
    }

    return pages;
  };

  const pages = getPages();

  useEffect(() => {
    fetchHistoryData(1);
  }, []);



  const totalPages = 5;
       const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
  return (
    <div className="inter-font main-bg" style={{ minHeight: "100vh" }}>
      <div
        className="inter-font bg-white rounded-3 m-5 d-flex flex-column"
        style={{ minHeight: "79vh", overflow: "hidden" }}
      >
       <div 
  className={`inter-font border-bottom py-3 ${isRTL ? 'me-4' : 'ms-4'}`}
  style={{
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left'
  }}
>
  <h3 
    className="inter-font pb-1" 
    style={{ 
      fontSize: "20px",
      textAlign: isRTL ? 'right' : 'left'
    }}
  >
    {t("t_histroy_text")} {/* My History */}
  </h3>
</div>
        <div className="inter-font scrollable-list flex-grow-1"   style={{
    overflowX: "auto", // 👈 Enables horizontal scroll
    whiteSpace: "nowrap",
  }}>

        { isLoading ? (

                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="inter-font spinner-border" role="status"></div>
                    </div>
                ) : historyData?.length === 0 ? (
                  <div className="d-flex flex-column align-items-center py-5">
                  <div style={{ width: 200 }}>
                    <Lottie animationData={NoDataAnimation} loop={true} />
                  </div>
                </div>
                ) : (
          historyData.map((item, index) => (
            <HistoryCard key={index} item={item} />
          )))}
        </div>

        {pagination?.total_pages > 1 && (
                <Paginator
                    page={pagination.current_page}
                    rows={pagination.per_page}
                    totalRecords={pagination.total_records}
                    onPageChange={handlePageChange}
                />
            )}
      </div>

      <style>{`
        .pagination-custom {
          display: flex;
          gap: 10px;
          align-items: center;
          justify-content: center;
          margin-top: 20px;
          padding: 0;
          list-style: none;
        }

        .pagination-custom .page-btn {
          background-color: transparent;
          border: none;
          font-size: 16px;
          color: #333;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          transition: 0.3s;
          cursor: pointer;
        }

        .pagination-custom .page-btn:hover {
          background-color: #f1f1f1;
        }

        .pagination-custom .page-btn.active {
          background-color: #e57c66;
          color: white;
          font-weight: bold;
        }

        .scrollable-list {
          max-height: 550px;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 10px;
        }

        .scrollable-list::-webkit-scrollbar {
          width: 6px;
        }

        .scrollable-list::-webkit-scrollbar-thumb {
          background-color: #c1c1c1;
          border-radius: 3px;
        }

        .scrollable-list::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default History;
